import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, UserPlus, Users, Shield, Clock, Trash2, Mail, ChevronDown, ChevronUp, Activity, RotateCcw, Star, MessageSquare, Copy, Link2, Award, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  owner: { bg: 'bg-amber-900/40', border: 'border-amber-600/50', text: 'text-amber-400', badge: 'bg-amber-600' },
  beta_tester: { bg: 'bg-stone-800/40', border: 'border-stone-600/50', text: 'text-stone-300', badge: 'bg-stone-600' },
  project_manager: { bg: 'bg-blue-900/40', border: 'border-blue-600/50', text: 'text-blue-400', badge: 'bg-blue-600' },
  grade_school_editor: { bg: 'bg-emerald-900/40', border: 'border-emerald-600/50', text: 'text-emerald-400', badge: 'bg-emerald-600' },
  theological_reviewer: { bg: 'bg-purple-900/40', border: 'border-purple-600/50', text: 'text-purple-400', badge: 'bg-purple-600' },
  padawan_tester: { bg: 'bg-cyan-900/40', border: 'border-cyan-600/50', text: 'text-cyan-400', badge: 'bg-cyan-600' },
  deliverance_editor: { bg: 'bg-rose-900/40', border: 'border-rose-600/50', text: 'text-rose-400', badge: 'bg-rose-600' },
};

const EDITORIAL_ROLES = [
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'grade_school_editor', label: 'Grade School Editor' },
  { value: 'theological_reviewer', label: 'Theological Reviewer' },
  { value: 'padawan_tester', label: "Master's Padawan" },
  { value: 'deliverance_editor', label: 'Deliverance Editor' },
];

const ROLE_DESCRIPTIONS = {
  project_manager: 'Near-full access to content editing and publishing. Sees the forest and the trees.',
  grade_school_editor: 'Edits grade school summaries — making the Word accessible to children.',
  theological_reviewer: 'Reviews and edits theological content — GIT panels, Three Voices, scripture arguments.',
  padawan_tester: "Master's Padawan — learns the curriculum, provides feedback and ratings.",
  deliverance_editor: 'Edits pastoral and deliverance content — opening paragraphs and pastoral voice.',
};

export default function TeamManagement() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [activitySummary, setActivitySummary] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [betaTesters, setBetaTesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');
  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [sending, setSending] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');
  // Promote modal
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promoteRole, setPromoteRole] = useState('');
  const [promoting, setPromoting] = useState(false);
  // Pending role change
  const [changingPendingFor, setChangingPendingFor] = useState(null);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [membersRes, invitesRes, rolesRes, feedRes, summaryRes, fbRes, btRes] = await Promise.all([
        fetch(`${API_URL}/api/team/members`, { headers: headers(), credentials: 'include' }),
        fetch(`${API_URL}/api/team/invitations`, { headers: headers(), credentials: 'include' }),
        fetch(`${API_URL}/api/team/roles`),
        fetch(`${API_URL}/api/audit/feed?limit=20`, { headers: headers(), credentials: 'include' }),
        fetch(`${API_URL}/api/audit/summary?days=7`, { headers: headers(), credentials: 'include' }),
        fetch(`${API_URL}/api/audit/feedback`, { headers: headers(), credentials: 'include' }),
        fetch(`${API_URL}/api/team/beta-testers`, { headers: headers(), credentials: 'include' }),
      ]);
      if (membersRes.ok) setMembers((await membersRes.json()).members || []);
      if (invitesRes.ok) setInvitations((await invitesRes.json()).invitations || []);
      if (rolesRes.ok) setRoles((await rolesRes.json()).roles || []);
      if (feedRes.ok) setActivityFeed((await feedRes.json()).entries || []);
      if (summaryRes.ok) setActivitySummary(await summaryRes.json());
      if (fbRes.ok) setFeedback((await fbRes.json()).feedback || []);
      if (btRes.ok) setBetaTesters((await btRes.json()).beta_testers || []);
    } catch {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail || !inviteRole) {
      toast.error('Email and role are required');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/team/invite`, {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify({ email: inviteEmail, role: inviteRole, name: inviteName, note: inviteNote }),
      });
      if (res.ok) {
        const data = await res.json();
        const inviteLink = `${window.location.origin}/register?invite=${data.invite_token}`;
        toast.success(`Invitation created for ${inviteEmail}`);
        // Show the link in a prompt
        try { await navigator.clipboard.writeText(inviteLink); toast.success('Invitation link copied to clipboard!'); } catch { /* ignore */ }
        setLastInviteLink(inviteLink);
        setInviteEmail('');
        setInviteName('');
        setInviteRole('');
        setInviteNote('');
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send invitation');
      }
    } catch {
      toast.error('Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const revokeInvite = async (invitationId) => {
    try {
      const res = await fetch(`${API_URL}/api/team/invitations/${invitationId}`, {
        method: 'DELETE', headers: headers(), credentials: 'include',
      });
      if (res.ok) {
        toast.success('Invitation revoked');
        loadData();
      }
    } catch {
      toast.error('Failed to revoke');
    }
  };

  const removeMember = async (email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/team/members/${email}`, {
        method: 'DELETE', headers: headers(), credentials: 'include',
      });
      if (res.ok) {
        toast.success('Member removed');
        loadData();
      }
    } catch {
      toast.error('Failed to remove member');
    }
  };

  const promoteMember = async (email, targetRole) => {
    setPromoting(true);
    try {
      const body = targetRole ? { role: targetRole } : {};
      const res = await fetch(`${API_URL}/api/team/promote/${email}`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${email} promoted to ${data.new_role_label}!`);
        setPromoteTarget(null);
        setPromoteRole('');
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to promote');
      }
    } catch {
      toast.error('Failed to promote');
    } finally {
      setPromoting(false);
    }
  };

  const updatePendingRole = async (email, newPendingRole) => {
    try {
      const res = await fetch(`${API_URL}/api/team/members/${email}/pending-role`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({ pending_role: newPendingRole }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Pending role updated to ${data.pending_role_label}`);
        setChangingPendingFor(null);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update pending role');
      }
    } catch {
      toast.error('Failed to update pending role');
    }
  };

  const formatTime = (iso) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    return d.toLocaleDateString();
  };

  const tabs = [
    { id: 'members', label: 'Team', icon: Users },
    { id: 'beta', label: 'Beta Testers', icon: Award },
    { id: 'invite', label: 'Invite', icon: UserPlus },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-amber-400 animate-pulse text-lg">Loading team data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-stone-200">
      {/* Header */}
      <div className="border-b border-stone-800 bg-[#0d0d14]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            data-testid="team-back-btn"
            onClick={() => navigate('/admin/lessons')}
            className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-stone-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-amber-400 tracking-wide">THE COUNCIL</h1>
            <p className="text-xs text-stone-500">Team Management & Activity Oversight</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-stone-500">{members.length} members</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-stone-800 bg-[#0d0d14]/80">
        <div className="max-w-6xl mx-auto px-4 flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`team-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-500 text-amber-400'
                  : 'border-transparent text-stone-500 hover:text-stone-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* ── Team Members Tab ── */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-stone-300">Team Members</h2>
            <div className="grid gap-3">
              {members.map(m => {
                const role = m.team_role || 'owner';
                const colors = ROLE_COLORS[role] || ROLE_COLORS.owner;
                return (
                  <div
                    key={m.email}
                    data-testid={`team-member-${m.email}`}
                    className={`${colors.bg} ${colors.border} border rounded-lg p-4 flex items-center gap-4`}
                  >
                    <div className={`w-10 h-10 rounded-full ${colors.badge} flex items-center justify-center text-white font-bold text-sm`}>
                      {(m.name || m.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-stone-200 truncate">{m.name || 'Unnamed'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${colors.badge} text-white`}>
                          {m.team_role_label || 'Owner'}
                        </span>
                        {m.pending_editorial_role && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-stone-800 text-stone-400 border border-stone-700">
                            Pending: {m.pending_editorial_role_label}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 truncate">{m.email}</p>
                      {m.team_role_assigned_at && (
                        <p className="text-xs text-stone-600 mt-0.5">Joined {formatTime(m.team_role_assigned_at)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {role === 'beta_tester' && (
                        <button
                          data-testid={`promote-member-${m.email}`}
                          onClick={() => { setPromoteTarget(m); setPromoteRole(m.pending_editorial_role || ''); }}
                          className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-xs rounded-lg font-medium transition-colors flex items-center gap-1"
                        >
                          <Zap className="w-3 h-3" /> Promote
                        </button>
                      )}
                      {role !== 'owner' && (
                        <button
                          data-testid={`remove-member-${m.email}`}
                          onClick={() => removeMember(m.email)}
                          className="p-2 text-stone-600 hover:text-red-400 transition-colors"
                          title="Remove from team"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {members.length === 0 && (
                <div className="text-center py-12 text-stone-600">
                  No team members yet. Send invitations to get started.
                </div>
              )}
            </div>

            {/* Pending Invitations */}
            {invitations.filter(i => i.status === 'pending').length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-semibold text-stone-400 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Pending Invitations
                </h3>
                <div className="space-y-2">
                  {invitations.filter(i => i.status === 'pending').map(inv => {
                    const colors = ROLE_COLORS[inv.role] || ROLE_COLORS.padawan_tester;
                    return (
                      <div
                        key={inv.invitation_id}
                        data-testid={`pending-invite-${inv.email}`}
                        className="bg-stone-900/50 border border-stone-800 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-stone-500" />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-stone-300">{inv.name || inv.email}</span>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${colors.badge} text-white`}>
                              {inv.role_label}
                            </span>
                            {inv.note && <p className="text-xs text-stone-600 mt-0.5">{inv.note}</p>}
                          </div>
                          <span className="text-xs text-stone-600">{formatTime(inv.created_at)}</span>
                          <button
                            onClick={() => {
                              const link = `${window.location.origin}/register?invite=${inv.invite_token}`;
                              navigator.clipboard.writeText(link);
                              toast.success('Invite link copied!');
                            }}
                            className="px-2 py-1 text-xs bg-stone-800 hover:bg-stone-700 text-stone-300 rounded flex items-center gap-1"
                          >
                            <Copy className="w-3 h-3" /> Copy Link
                          </button>
                          <button
                            data-testid={`revoke-invite-${inv.invitation_id}`}
                            onClick={() => revokeInvite(inv.invitation_id)}
                            className="text-xs text-red-500/60 hover:text-red-400 transition-colors"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Beta Testers Tab ── */}
        {activeTab === 'beta' && (
          <div className="space-y-6" data-testid="beta-testers-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-stone-300">Beta Tester Management</h2>
                <p className="text-sm text-stone-500 mt-1">
                  Manage your beta testers — change their track, promote to editorial roles, or monitor progress.
                </p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-stone-800/60 rounded-lg border border-stone-700/50">
                <Award className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-stone-300 font-medium">{betaTesters.length} tester{betaTesters.length !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {betaTesters.length === 0 ? (
              <div className="text-center py-16 rounded-xl border border-stone-800/50 bg-stone-900/30">
                <Award className="w-10 h-10 mx-auto text-stone-700 mb-3" />
                <p className="text-stone-500 mb-2">No beta testers yet</p>
                <p className="text-xs text-stone-600">Invite team members — they start as Beta Testers before being promoted.</p>
                <button onClick={() => setActiveTab('invite')} className="mt-4 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-lg transition-colors" data-testid="go-to-invite-btn">
                  Invite Someone
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {betaTesters.map(bt => {
                  const pendingColors = ROLE_COLORS[bt.pending_editorial_role] || ROLE_COLORS.beta_tester;
                  const isChangingPending = changingPendingFor === bt.email;
                  return (
                    <div key={bt.email} data-testid={`beta-tester-card-${bt.email}`}
                      className="bg-stone-900/60 border border-stone-800 rounded-xl p-5 transition-all hover:border-stone-700">
                      {/* Top row: name + badges */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-full bg-stone-700 flex items-center justify-center text-white font-bold text-sm">
                            {(bt.name || bt.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <span className="font-medium text-stone-200 block">{bt.name || 'Unnamed'}</span>
                            <span className="text-xs text-stone-500">{bt.email}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-stone-600 text-white">Beta Tester</span>
                          {bt.pending_editorial_role && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${pendingColors.badge} text-white`}>
                              Track: {bt.pending_editorial_role_label}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stats row */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-stone-800/60 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-amber-400">{bt.feedback_count || 0}</p>
                          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Feedback</p>
                        </div>
                        <div className="bg-stone-800/60 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-emerald-400">{bt.lessons_completed || 0}</p>
                          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Completed</p>
                        </div>
                        <div className="bg-stone-800/60 rounded-lg p-3 text-center">
                          <p className="text-xs font-medium text-stone-300">{bt.last_activity ? formatTime(bt.last_activity) : 'Never'}</p>
                          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Last Active</p>
                        </div>
                      </div>

                      {/* Pending role changer */}
                      {isChangingPending ? (
                        <div className="bg-stone-800/40 rounded-lg p-3 mb-3 border border-stone-700/50" data-testid={`pending-role-picker-${bt.email}`}>
                          <p className="text-xs text-stone-400 mb-2 font-medium">Change editorial track:</p>
                          <div className="flex flex-wrap gap-2">
                            {EDITORIAL_ROLES.map(r => (
                              <button key={r.value} onClick={() => updatePendingRole(bt.email, r.value)}
                                className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                                  bt.pending_editorial_role === r.value
                                    ? 'bg-amber-600/20 border-amber-600/50 text-amber-400'
                                    : 'bg-stone-800 border-stone-700 text-stone-300 hover:border-stone-600'
                                }`}
                                data-testid={`set-pending-${r.value}`}>
                                {r.label}
                              </button>
                            ))}
                          </div>
                          <button onClick={() => setChangingPendingFor(null)} className="text-xs text-stone-600 mt-2 hover:text-stone-400">Cancel</button>
                        </div>
                      ) : null}

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 pt-2 border-t border-stone-800/50">
                        <button
                          data-testid={`change-track-${bt.email}`}
                          onClick={() => setChangingPendingFor(isChangingPending ? null : bt.email)}
                          className="px-3 py-1.5 text-xs bg-stone-800 hover:bg-stone-700 text-stone-300 rounded-lg border border-stone-700 transition-colors flex items-center gap-1.5"
                        >
                          <ChevronDown className="w-3 h-3" /> Change Track
                        </button>
                        <button
                          data-testid={`promote-beta-${bt.email}`}
                          onClick={() => { setPromoteTarget(bt); setPromoteRole(bt.pending_editorial_role || ''); }}
                          className="px-3 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg transition-colors flex items-center gap-1.5 font-medium"
                        >
                          <Zap className="w-3 h-3" /> Promote Now
                        </button>
                        <button
                          data-testid={`remove-beta-${bt.email}`}
                          onClick={() => removeMember(bt.email)}
                          className="ml-auto p-1.5 text-stone-600 hover:text-red-400 transition-colors rounded"
                          title="Remove from team"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Promote Modal ── */}
        {promoteTarget && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="promote-modal-overlay"
            onClick={e => { if (e.target === e.currentTarget) { setPromoteTarget(null); setPromoteRole(''); } }}>
            <div className="bg-[#0d0d14] border border-stone-700 rounded-xl max-w-md w-full p-6" data-testid="promote-modal">
              <h3 className="text-lg font-bold text-amber-400 mb-1">Promote Beta Tester</h3>
              <p className="text-sm text-stone-400 mb-5">
                Promote <span className="text-stone-200 font-medium">{promoteTarget.name || promoteTarget.email}</span> to an editorial role.
              </p>
              <div className="space-y-2 mb-5">
                {EDITORIAL_ROLES.map(r => {
                  const colors = ROLE_COLORS[r.value] || ROLE_COLORS.padawan_tester;
                  const selected = promoteRole === r.value;
                  return (
                    <button key={r.value} onClick={() => setPromoteRole(r.value)}
                      data-testid={`promote-role-${r.value}`}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selected
                          ? `${colors.bg} ${colors.border} border-2`
                          : 'bg-stone-900/30 border-stone-800 hover:border-stone-700'
                      }`}>
                      <span className={`text-sm font-medium ${selected ? colors.text : 'text-stone-300'}`}>
                        {r.label}
                      </span>
                      {selected && <span className="text-xs text-amber-500 ml-2">Selected</span>}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="confirm-promote-btn"
                  onClick={() => promoteMember(promoteTarget.email, promoteRole)}
                  disabled={!promoteRole || promoting}
                  className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Zap className="w-4 h-4" /> {promoting ? 'Promoting...' : 'Confirm Promotion'}
                </button>
                <button
                  data-testid="cancel-promote-btn"
                  onClick={() => { setPromoteTarget(null); setPromoteRole(''); }}
                  className="px-4 py-2.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-300 text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Invite Tab ── */}
        {activeTab === 'invite' && (
          <div className="max-w-lg space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-stone-300">Invite Team Member</h2>
              <p className="text-sm text-stone-500 mt-1">
                They sign up as an Explorer first. Once invited, they'll see the invitation when they log in.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-stone-400 mb-1.5">Name</label>
                <input
                  data-testid="invite-name-input"
                  type="text"
                  value={inviteName}
                  onChange={e => setInviteName(e.target.value)}
                  placeholder="e.g. Tami"
                  className="w-full bg-stone-900/60 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-200 placeholder-stone-600 focus:border-amber-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-stone-400 mb-1.5">Email</label>
                <input
                  data-testid="invite-email-input"
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="e.g. tami@example.com"
                  className="w-full bg-stone-900/60 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-200 placeholder-stone-600 focus:border-amber-600 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs text-stone-400 mb-1.5">Role</label>
                <div className="space-y-2">
                  {roles.map(r => {
                    const colors = ROLE_COLORS[r.role] || ROLE_COLORS.padawan_tester;
                    const selected = inviteRole === r.role;
                    return (
                      <button
                        key={r.role}
                        data-testid={`role-option-${r.role}`}
                        onClick={() => setInviteRole(r.role)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selected
                            ? `${colors.bg} ${colors.border} border-2`
                            : 'bg-stone-900/30 border-stone-800 hover:border-stone-700'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${selected ? colors.text : 'text-stone-300'}`}>
                            {r.label}
                          </span>
                          {selected && <span className="text-xs text-amber-500 ml-auto">Selected</span>}
                        </div>
                        <p className="text-xs text-stone-500 mt-1">{ROLE_DESCRIPTIONS[r.role]}</p>
                        {r.editable_fields && r.editable_fields.length > 0 && (
                          <p className="text-xs text-stone-600 mt-1">
                            Can edit: {Array.isArray(r.editable_fields) ? r.editable_fields.join(', ') : 'All fields'}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs text-stone-400 mb-1.5">Note (optional)</label>
                <input
                  data-testid="invite-note-input"
                  type="text"
                  value={inviteNote}
                  onChange={e => setInviteNote(e.target.value)}
                  placeholder="e.g. Focus on grade school summaries for VOS"
                  className="w-full bg-stone-900/60 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-200 placeholder-stone-600 focus:border-amber-600 focus:outline-none"
                />
              </div>

              <button
                data-testid="send-invite-btn"
                onClick={sendInvite}
                disabled={sending || !inviteEmail || !inviteRole}
                className="w-full py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sending ? 'Creating...' : 'Create Invitation Link'}
              </button>

              {/* Show invite link after creation */}
              {lastInviteLink && (
                <div data-testid="invite-link-display" className="p-3 rounded-lg bg-emerald-900/20 border border-emerald-600/30">
                  <p className="text-xs text-emerald-400 font-bold mb-2 flex items-center gap-1">
                    <Link2 className="w-3 h-3" /> Invitation Link Created
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={lastInviteLink}
                      className="flex-1 bg-stone-900/60 border border-stone-700 rounded px-2 py-1.5 text-xs text-stone-300 font-mono"
                      onClick={e => e.target.select()}
                    />
                    <button
                      data-testid="copy-invite-link-btn"
                      onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success('Copied!'); }}
                      className="px-2 py-1.5 bg-stone-800 hover:bg-stone-700 rounded text-xs text-stone-300 flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">
                    Share this link with your team member. They'll register and be automatically assigned their role.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Activity Tab ── */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            {activitySummary && activitySummary.members && activitySummary.members.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-stone-400 mb-3">Last 7 Days Summary</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {activitySummary.members.map(m => {
                    const colors = ROLE_COLORS[m.role] || ROLE_COLORS.owner;
                    return (
                      <div key={m.email} className={`${colors.bg} ${colors.border} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-stone-200">{m.email.split('@')[0]}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${colors.badge} text-white`}>{m.role}</span>
                        </div>
                        <div className="flex gap-4 text-xs text-stone-400">
                          <span>{m.edit_count} edits</span>
                          <span>{m.lessons_touched} lessons</span>
                        </div>
                        <p className="text-xs text-stone-600 mt-1">Last active: {formatTime(m.last_active)}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Activity Feed */}
            <div>
              <h3 className="text-sm font-semibold text-stone-400 mb-3">Recent Activity</h3>
              <div className="space-y-2">
                {activityFeed.map((entry, i) => (
                  <div key={entry.audit_id || i} className="flex items-start gap-3 bg-stone-900/30 border border-stone-800/50 rounded-lg p-3">
                    <div className="w-6 h-6 rounded-full bg-stone-800 flex items-center justify-center text-xs text-stone-400 mt-0.5 shrink-0">
                      {(entry.user_name || entry.user_email || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-stone-300 font-medium">{entry.user_name || entry.user_email}</span>
                        <span className="text-xs text-stone-600">{entry.action.replace('_', ' ')}</span>
                        <span className="text-xs text-amber-500/80 font-mono">{entry.module_key}</span>
                      </div>
                      {entry.changes && entry.changes.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {entry.changes.slice(0, 3).map((c, ci) => (
                            <p key={ci} className="text-xs text-stone-500">
                              Changed <span className="text-stone-400">{c.field}</span>
                            </p>
                          ))}
                          {entry.changes.length > 3 && (
                            <p className="text-xs text-stone-600">+{entry.changes.length - 3} more changes</p>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-stone-600 shrink-0">{formatTime(entry.timestamp)}</span>
                  </div>
                ))}
                {activityFeed.length === 0 && (
                  <div className="text-center py-12 text-stone-600">
                    No activity yet. Edits will appear here as team members work.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Feedback Tab ── */}
        {activeTab === 'feedback' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-stone-300">Beta Tester Feedback</h2>
            <p className="text-sm text-stone-500">Structured responses from team members as they work through lessons.</p>
            <div className="space-y-3">
              {feedback.map(fb => {
                const colors = ROLE_COLORS[fb.user_role] || ROLE_COLORS.padawan_tester;
                return (
                  <div key={fb.feedback_id} data-testid={`feedback-card-${fb.feedback_id}`}
                    className={`${colors.bg} ${colors.border} border rounded-lg p-4`}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm font-medium text-stone-300">{fb.user_name || fb.user_email}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${colors.badge} text-white`}>
                        {fb.user_role}
                      </span>
                      <span className="text-xs text-stone-500 font-mono">{fb.module_key}</span>
                      {fb.rating > 0 && (
                        <div className="flex items-center gap-0.5 ml-auto">
                          {Array.from({length: 5}).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < fb.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-700'}`} />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Structured Responses */}
                    {fb.think_response && (
                      <div className="mb-2 pl-3 border-l-2 border-amber-600/30">
                        <p className="text-xs text-amber-500/80 font-bold">What this caused me to think:</p>
                        <p className="text-sm text-stone-300">{fb.think_response}</p>
                      </div>
                    )}
                    {fb.improve_response && (
                      <div className="mb-2 pl-3 border-l-2 border-blue-600/30">
                        <p className="text-xs text-blue-400/80 font-bold">What could be resaid:</p>
                        <p className="text-sm text-stone-300">{fb.improve_response}</p>
                      </div>
                    )}
                    {fb.comment && (
                      <div className="mb-2 pl-3 border-l-2 border-stone-700">
                        <p className="text-xs text-stone-500 font-bold">Additional notes:</p>
                        <p className="text-sm text-stone-400">{fb.comment}</p>
                      </div>
                    )}
                    <p className="text-xs text-stone-600 mt-2">{formatTime(fb.created_at || fb.updated_at)}</p>
                  </div>
                );
              })}
              {feedback.length === 0 && (
                <div className="text-center py-12 text-stone-600">
                  No feedback submitted yet. As team members go through lessons, their structured responses will appear here.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
